import React, { useState, useEffect } from 'react';
import { Table, Input, Switch, Tag, Typography, Card, Avatar, message, Tooltip, Progress } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const BlockchainLogosContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const StyledTableCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  /* Fix pagination text colors for dark theme */
  .ant-pagination {
    color: ${COLORS.textPrimary};
  }
  
  .ant-pagination-total-text {
    color: ${COLORS.textSecondary} !important;
  }
  
  .ant-pagination-item {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    
    a {
      color: ${COLORS.textPrimary} !important;
    }
    
    &:hover {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-pagination-item-active {
    background: ${COLORS.accent} !important;
    border-color: ${COLORS.accent} !important;
    
    a {
      color: #000 !important;
    }
  }
  
  .ant-pagination-prev, .ant-pagination-next {
    .ant-pagination-item-link {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    &:hover .ant-pagination-item-link {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-pagination-disabled {
    .ant-pagination-item-link {
      color: ${COLORS.textSecondary} !important;
    }
  }
  
  .ant-pagination-options {
    .ant-select-selector {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-select-arrow {
      color: ${COLORS.textSecondary} !important;
    }
  }
  
  .ant-table {
    background: transparent !important;
    color: ${COLORS.textPrimary};
    
    @media (max-width: 768px) {
      min-width: 800px;
    }
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    @media (max-width: 768px) {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }
  }
  
  /* Fixed column styling for dark theme */
  .ant-table-thead > tr > th.ant-table-cell-fix-left {
    background: ${COLORS.bgPrimary} !important;
  }
  
  .ant-table-tbody > tr > td.ant-table-cell-fix-left {
    background: ${COLORS.bgSecondary} !important;
  }
  
  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
    background: rgba(255, 255, 255, 0.04) !important;
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${COLORS.border} !important;
    
    @media (max-width: 768px) {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: rgba(255, 255, 255, 0.02) !important;
  }
`;

interface CurrencyItem {
  id: string;
  name: string;
  symbol: string;
  iconUrl?: string;
  status: string;
  totalContracts: number;
  supportedContracts: number;
  blockchains: Array<{
    id: string;
    name: string;
    abbreviation?: string;
    iconUrl?: string;
  }>;
  isSupported: boolean;
  isFullySupported: boolean;
}

const SupportedCurrencies: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<CurrencyItem[]>([]);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  useEffect(() => {
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pagination.current, pagination.pageSize]);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId: pagination.current,
        perPage: pagination.pageSize,
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_SUPPORTED_CURRENCIES, {
        params,
      });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCurrencies(items);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination(prev => ({
            ...prev,
            total: paginator.itemCount || paginator.totalCount || paginator.total || 0,
          }));
        }
      }
    } catch (error: any) {
      console.error('Error fetching supported currencies:', error);
      message.error('Failed to load supported currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleToggleSupport = async (currencyId: string, isSupported: boolean) => {
    try {
      setTogglingId(currencyId);
      const endpoint = API_ENDPOINTS.MERCHANT_TOGGLE_SUPPORTED_CURRENCY.replace('{currencyId}', currencyId);
      await apiClient.put(endpoint, {
        isSupported: !isSupported,
      });
      message.success(`Currency ${!isSupported ? 'enabled' : 'disabled'} successfully`);
      fetchCurrencies();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update support status');
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      title: 'Enabled',
      key: 'isSupported',
      width: 80,
      fixed: 'left' as const,
      render: (_: any, record: CurrencyItem) => (
        <Tooltip 
          title={record.isFullySupported 
            ? 'Click to disable all contracts for this currency'
            : 'Click to enable all contracts for this currency'
          }
        >
          <Switch
            checked={record.isFullySupported}
            loading={togglingId === record.id}
            onChange={() => handleToggleSupport(record.id, record.isFullySupported)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Currency',
      key: 'currency',
      render: (_: any, record: CurrencyItem) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {record.iconUrl && (
            <Avatar src={record.iconUrl} size={40} />
          )}
          <div>
            <div style={{ fontWeight: 600, fontSize: '15px' }}>{record.name}</div>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
              {record.symbol}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Available On',
      key: 'blockchains',
      render: (_: any, record: CurrencyItem) => {
        const blockchains = record.blockchains || [];
        if (blockchains.length === 0) return <Text type="secondary">-</Text>;
        
        return (
          <BlockchainLogosContainer>
            {blockchains.slice(0, 6).map((chain: any) => (
              <Tooltip key={chain.id} title={chain.name || chain.abbreviation}>
                <Avatar 
                  src={chain.iconUrl} 
                  size={24}
                  style={{ border: `1px solid ${COLORS.border}` }}
                >
                  {!chain.iconUrl && (chain.abbreviation?.[0] || '?')}
                </Avatar>
              </Tooltip>
            ))}
            {blockchains.length > 6 && (
              <Tooltip title={blockchains.slice(6).map((c: any) => c.name).join(', ')}>
                <Tag style={{ marginLeft: 4 }}>+{blockchains.length - 6}</Tag>
              </Tooltip>
            )}
          </BlockchainLogosContainer>
        );
      },
    },
    {
      title: 'Contracts',
      key: 'contracts',
      width: 150,
      render: (_: any, record: CurrencyItem) => {
        const percent = record.totalContracts > 0 
          ? Math.round((record.supportedContracts / record.totalContracts) * 100)
          : 0;
        
        return (
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text style={{ fontSize: '13px' }}>
                {record.supportedContracts} / {record.totalContracts} enabled
              </Text>
            </div>
            <Progress 
              percent={percent} 
              size="small"
              strokeColor={record.isFullySupported ? '#52c41a' : COLORS.accent}
              showInfo={false}
            />
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Supported Currencies
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Enable or disable currencies. Use "Support All" to enable/disable all contract addresses for a currency at once.
        </Text>
      </HeaderContainer>

      <FiltersContainer>
        <Input
          placeholder="Search currencies by name or symbol..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 400, width: '100%' }}
          allowClear
        />
      </FiltersContainer>

      <TableScrollWrapper>
        <StyledTableCard>
          <Table
            columns={columns}
            dataSource={currencies}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (total) => <span style={{ color: COLORS.textSecondary }}>Total {total} currencies</span>,
              responsive: true,
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </StyledTableCard>
      </TableScrollWrapper>
    </PageContainer>
  );
};

export default SupportedCurrencies;
